import RaffleDealAllHisotryJSC from '@/schema/RaffleDealAllHisotryJSC';
import RaffleDealParticipantsCountJSC from '@/schema/RaffleDealParticipantsCountJSC';
import RaffleDealParticipateJSC from '@/schema/RaffleDealParticipateJSC';
import RaffleDealParticipationHistoryJSC from '@/schema/RaffleDealParticipationHistoryJSC';
import RaffleDealPromotionsJSC from '@/schema/RaffleDealPromotionsJSC';
import RaffleDealWinHistoryJSC from '@/schema/RaffleDealWinHistoryJSC';
import RaffleDealWinnersJSC from '@/schema/RaffleDealWinnersJSC';
import { TReqConfigValidated } from '@/services/JSCService';

import { PromotionPlatformApiRequestBuilder } from '../Common/PromotionPlatformApiRequestBuilder';
import { IRaffleDealAllHisotryRequestParams } from './interfaces/IRaffleDealAllHistoryRequestParams';
import { IRaffleDealParticipantsCountRequestParams } from './interfaces/IRaffleDealParticipantsCountRequestParams';
import { IRaffleDealParticipateRequestParams } from './interfaces/IRaffleDealParticipateRequestParams';
import { IRaffleDealParticipationHistoryRequestParams } from './interfaces/IRaffleDealParticipationHistoryRequestParams';
import { IRaffleDealPromotionsRequestParams } from './interfaces/IRaffleDealPromotionsRequestParams';
import { IRaffleDealWinHistoryRequestParams } from './interfaces/IRaffleDealWinHistoryRequestParams';
import { IRaffleDealWinnersRequestParams } from './interfaces/IRaffleDealWinnersRequestParams';

export class RaffleDealRequestBuilder extends PromotionPlatformApiRequestBuilder {
  /**
   * 프로모션 정보 조회
   */
  getPromotions(reqParams: IRaffleDealPromotionsRequestParams): TReqConfigValidated {
    return this.validate(RaffleDealPromotionsJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/api/v2/raffle-promotions', params),
    );
  }

  /**
   * 전체 내역 조회하기
   */
  getAllHistory(reqParams: IRaffleDealAllHisotryRequestParams): TReqConfigValidated {
    return this.validate(RaffleDealAllHisotryJSC, reqParams).map((params) =>
      this.makeConfig('GET', `/api/v2/raffle-promotions/${params.resources.groupCode}/all-histories`, params),
    );
  }

  /**
   * 응모 내역 조회하기
   */
  getParticipationHistory(reqParams: IRaffleDealParticipationHistoryRequestParams): TReqConfigValidated {
    return this.validate(RaffleDealParticipationHistoryJSC, reqParams).map((params) =>
      this.makeConfig('GET', `/api/v2/raffle-promotions/${params.resources.groupCode}/participation-histories`, params),
    );
  }

  /**
   * 당첨 내역 조회하기
   */
  getGroupWinHistory(reqParams: IRaffleDealWinHistoryRequestParams): TReqConfigValidated {
    return this.validate(RaffleDealWinHistoryJSC, reqParams).map((params) =>
      this.makeConfig('GET', `/api/v2/raffle-promotions/${params.resources.groupCode}/group-win-histories`, params),
    );
  }

  /**
   * 당첨자 목록 조회하기
   */
  getWinners(reqParams: IRaffleDealWinnersRequestParams): TReqConfigValidated {
    return this.validate(RaffleDealWinnersJSC, reqParams).map((params) =>
      this.makeConfig(
        'GET',
        `/api/v2/raffle-promotions/${params.resources.promotionId}/deals/${params.resources.dealId}/winners`,
        params,
      ),
    );
  }

  // --------------------------------------------------------------------
  // ------------------------------ V3 API ------------------------------
  // --------------------------------------------------------------------

  /**
   * 래플딜 프로모션 API v3 - 1. 프로모션 정보 조회
   */
  createGetRafflePromotions(reqParams: IRaffleDealPromotionsRequestParams): TReqConfigValidated {
    return this.validate(RaffleDealPromotionsJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/api/v3/raffle-promotions', params),
    );
  }

  /**
   * 래플딜 프로모션 API v3 - 2. 응모하기
   */
  createPostParticipate(reqParams: IRaffleDealParticipateRequestParams): TReqConfigValidated {
    return this.validate(RaffleDealParticipateJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/api/v3/raffle-promotions/participate', params),
    );
  }

  /**
   * 래플딜 프로모션 API v3 - 3. 전체내역 조회하기
   */
  createGetAllHistory(reqParams: IRaffleDealAllHisotryRequestParams): TReqConfigValidated {
    return this.validate(RaffleDealAllHisotryJSC, reqParams).map((params) =>
      this.makeConfig('GET', `/api/v3/raffle-promotions/${params.resources.groupCode}/all-histories`, params),
    );
  }

  /**
   * 래플딜 프로모션 API v3 - 4. 사용자별 응모내역 조회하기
   */
  createGetParticipationHistory(reqParams: IRaffleDealParticipationHistoryRequestParams): TReqConfigValidated {
    return this.validate(RaffleDealParticipationHistoryJSC, reqParams).map((params) =>
      this.makeConfig('GET', `/api/v3/raffle-promotions/${params.resources.groupCode}/participation-histories`, params),
    );
  }

  /**
   * 래플딜 프로모션 API v3 - 5. 사용자별 당첨내역 조회
   */
  createGetWinHistory(reqParams: IRaffleDealWinHistoryRequestParams): TReqConfigValidated {
    return this.validate(RaffleDealWinHistoryJSC, reqParams).map((params) =>
      this.makeConfig('GET', `/api/v3/raffle-promotions/${params.resources.groupCode}/win-histories`, params),
    );
  }

  /**
   * 래플딜 프로모션 API v3 - 6. Deal별 당첨자 목록 조회
   */
  createGetWinners(reqParams: IRaffleDealWinnersRequestParams): TReqConfigValidated {
    return this.validate(RaffleDealWinnersJSC, reqParams).map((params) =>
      this.makeConfig(
        'GET',
        `/api/v3/raffle-promotions/${params.resources.promotionId}/deals/${params.resources.dealId}/winners`,
        params,
      ),
    );
  }

  /**
   * 래플딜 프로모션 API v3 - 7. 응모자수 확인
   */
  createGetParticipantsCount(reqParams: IRaffleDealParticipantsCountRequestParams): TReqConfigValidated {
    return this.validate(RaffleDealParticipantsCountJSC, reqParams).map((params) =>
      this.makeConfig(
        'GET',
        `/api/v3/raffle-promotions/${params.resources.promotionId}/deals/${params.resources.dealId}/participants/count`,
        params,
      ),
    );
  }

  protected getAPIEndpoint(path: string): string {
    const baseURI = this.baseURI.href();
    return baseURI ? `${baseURI}${path}` : `${baseURI}/api/v1${path}`;
  }
}
